import { useState } from 'react';
import { isAxiosError } from 'axios';
import { Loader2, Router as RouterIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useT } from '@/contexts/I18nContext';
import { upsertZoneRouter, type RouterUpsert } from '@/api/zoneRouter';
import {
  ROUTER_MODEL_DROPDOWN_VALUES,
  ROUTER_MODEL_DROPDOWN_LABEL_KEYS,
  resolveRouterModelName,
  type RouterModelDropdownValue,
} from './routerModelOptions';
import { extractApiError } from '@/utils/apiError';

interface Props {
  zoneId: number | string;
  onCreated: () => void;
  onCancel: () => void;
  /** Pre-fill the gateway IP + admin URL on create. Used by the LC
   *  peer-discovery flow to seed the form from a discovered host. */
  initialGatewayIp?: string;
}

/**
 * Inline new-router form used by `AddDeviceDropdown → Router`.
 *
 * Field set mirrors the `RouterForm` block inside `RouterPanel.tsx`. We
 * intentionally duplicate rather than extract to keep PR-1 surgical —
 * `RouterPanel.tsx` is touched in PR-2 alongside the empty-state fix.
 * If a third caller appears, extract to a shared `<RouterForm>` then.
 *
 * Persists via the same `PUT /equipment_discovery/zones/<id>/router/`
 * endpoint as the existing edit flow. The BE is upsert-shaped, so
 * "create" and "edit" share one path.
 */
export default function NewRouterCard({
  zoneId,
  onCreated,
  onCancel,
  initialGatewayIp,
}: Props) {
  const { t } = useT();
  const [modelDropdown, setModelDropdown] = useState<RouterModelDropdownValue>('keenetic');
  const [modelOtherText, setModelOtherText] = useState('');
  const [gatewayIp, setGatewayIp] = useState(initialGatewayIp ?? '');
  // Seed the admin URL from the same host — Keenetic admin lives at
  // http://<gateway-ip>/ in the typical out-of-box config, so this is
  // the right default for a freshly-discovered host the operator just
  // labeled as a router.
  const [adminUrl, setAdminUrl] = useState(
    initialGatewayIp ? `http://${initialGatewayIp}` : '',
  );
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const payload: RouterUpsert = {
      model_name: resolveRouterModelName(modelDropdown, modelOtherText),
      gateway_ip: gatewayIp.trim() || null,
      admin_url: adminUrl.trim(),
      // remote_access_url intentionally omitted — duplicate of admin_url.
      username,
      password,
      notes,
    };
    try {
      await upsertZoneRouter(zoneId, payload);
      onCreated();
    } catch (e) {
      if (isAxiosError(e)) {
        setError(extractApiError(e, t('error.general')));
      } else {
        setError(t('error.general'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <RouterIcon className="size-4" /> {t('equipment.newRouter.title')}
        </h2>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-router-model">{t('equipment.router.modelName')}</Label>
              <Select
                id="new-router-model"
                value={modelDropdown}
                onChange={e => setModelDropdown(e.target.value as RouterModelDropdownValue)}
              >
                {ROUTER_MODEL_DROPDOWN_VALUES.map(v => (
                  <option key={v} value={v}>{t(ROUTER_MODEL_DROPDOWN_LABEL_KEYS[v])}</option>
                ))}
              </Select>
              {modelDropdown === 'other' && (
                <Input
                  id="new-router-model-other"
                  value={modelOtherText}
                  onChange={e => setModelOtherText(e.target.value)}
                  placeholder={t('equipment.router.modelName.otherPlaceholder')}
                />
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-router-gw">{t('equipment.router.gatewayIp')}</Label>
              <Input
                id="new-router-gw"
                value={gatewayIp}
                onChange={e => setGatewayIp(e.target.value)}
                placeholder="192.168.1.1"
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label htmlFor="new-router-admin">{t('equipment.router.adminUrl')}</Label>
              <Input
                id="new-router-admin"
                value={adminUrl}
                onChange={e => setAdminUrl(e.target.value)}
                placeholder="http://192.168.1.1 or https://my.keenetic.net/…"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-router-user">{t('equipment.router.username')}</Label>
              <Input
                id="new-router-user"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="font-mono"
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-router-pw">{t('equipment.router.password')}</Label>
              <Input
                id="new-router-pw"
                name="router-new-secret"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="font-mono"
                autoComplete="new-password"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-router-notes">{t('equipment.router.notes')}</Label>
            <textarea
              id="new-router-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            />
          </div>
          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" onClick={submit} disabled={submitting}>
              {submitting && <Loader2 className="size-3.5 animate-spin" />}
              {t('common.save')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
